// Llamada vía softphone (Zoiper u otro). El esquema de protocolo es CONFIGURABLE
// porque según la versión Zoiper registra esquemas distintos (zoiper:, callto:, tel:,
// sip:). Se guarda en localStorage:
//   - dial_protocol: 'zoiper' | 'callto' | 'tel' | 'sip'   (default 'tel', el del teléfono o el programa que tenga el equipo)
//   - dial_prefijo_mas: '1' para anteponer '+' al número
//   - dial_pais: código de país por defecto para números nacionales (default '34')
// Mejoras vs la versión anterior:
//   - NO usa la detección por foco (document.hasFocus) que daba falsos "no hay Zoiper"
//     cuando Zoiper ya estaba abierto y marcaba sin robar el foco.
//   - Lanza el protocolo con un <a> temporal (no cambia window.location → no rompe la SPA).
//   - Copia el número al portapapeles como red de seguridad.
//
// B-580 (17-sep-2026) · Y LA LLAMADA QUEDA APUNTADA EN EL HILO DE WHATSAPP de ese teléfono, como nota interna (el dueño:
// «incluso que cuando se use el botón de LLAMAR que lo ponga»). Lo decide el servidor (POST
// /conversaciones/notas/llamada): si ese número no tiene conversación en la clínica, o quien llama no puede ver
// Conversaciones, no se apunta nada. NUNCA frena ni estropea la llamada: va detrás, sin esperar y sin avisar si falla.
// `conversacionId` (opcional): el hilo desde el que se llama, si se sabe.
//
// B-2233 (26-oct-2026) · TODOS LOS BOTONES «LLAMAR» A UN PACIENTE PASAN POR AQUÍ (el dueño: «sea desde donde sea con el
// botón de Llamar, da igual que sea desde comunicación, urgencias o desde su ficha, debe salir el aviso de llamada en su
// whatsapp»). El aviso es la NOTA INTERNA de arriba: al paciente no le llega nada, así que no cuenta como envío (ni
// horario de envíos, ni ventana de 24 h, ni plantilla). Urgencias y «A quién llamar» marcaban el número por su cuenta y
// no la dejaban; la ficha llamaba a su teléfono de contacto (el fijo, el Teléfono 1) y, si el WhatsApp del paciente
// estaba en otro número, no se encontraba su conversación. Ahora:
//   · `pacienteId` (opcional): la ficha a la que se llama. Si el número marcado no tiene conversación, se apunta en la
//     de esa ficha (lo busca el servidor, solo en esta clínica).
//   · DOS PULSACIONES SEGUIDAS SON UNA LLAMADA: el mismo número otra vez antes de DOBLE_PULSACION_MS no vuelve a marcar
//     ni a apuntar; y el servidor no repite la nota de quien llama al mismo número en la misma conversación en dos minutos.
// La guardia backend/routes/llamar-desde-cualquier-sitio.test.js comprueba que ningún botón «Llamar» a un paciente marca
// el número sin pasar por aquí.
//
// t513 (27-oct-2026, B-2246) · ANTES DE LLAMAR, SE PREGUNTA. El dueño: «cuando se pulsa el botón de llamar en la ficha, que pida
// confirmación». Decidido: aquí, en la función común, para que TODOS los «Llamar» de la aplicación pregunten igual (la ficha y
// sus seguimientos, la cita, Comunicación, Urgencias, Recordatorios, el inicio, las anuladas, «Hueco antes» y «A quién
// llamar»). Ninguno preguntaba por su cuenta, así que ninguno pregunta dos veces. La pregunta: «¿Llamar a <nombre> al
// <teléfono>?», con «Llamar» y «Cancelar», en el diálogo de la casa (context/DialogosContext · confirmar). Escape, «Cancelar»
// o tocar fuera NO llaman. Y:
//   · `nombre` (opcional): a quién se llama; sin él, «¿Llamar al <teléfono>?».
//   · MIENTRAS SE PREGUNTA, otro toque no abre otra pregunta (un doble clic = una pregunta); y la pregunta no se deja
//     contestar ni cerrar tocando fuera en su primer medio segundo (`pausaMs`): el segundo clic de un doble clic no la cierra
//     ni llama él solo.
//   · DOS PULSACIONES SEGUIDAS siguen siendo una llamada: el mismo número antes de DOBLE_PULSACION_MS desde la última
//     llamada ni pregunta ni vuelve a marcar.
//   · Devuelve una promesa: true si ha llamado.
// El diálogo se pide con un import DINÁMICO, como la api: este util sigue sin importar nada. Sin el diálogo (no debería pasar),
// el del navegador; sin ninguno, no se llama.
// Lo miden backend/routes/llamar-pregunta-antes.test.js y backend/routes/llamar-desde-cualquier-sitio.test.js.
import { t } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const DOBLE_PULSACION_MS = 1500
export const PAUSA_PREGUNTA_MS = 600
let ultima = { numero: '', at: 0 }
let preguntando = false

// «600 123 456» o «+34 600 123 456» para leerlo en la pregunta; lo demás, tal cual venga.
export function numeroParaLeer(tel) {
  const crudo = String(tel == null ? '' : tel).trim()
  const d = crudo.replace(/\D/g, '')
  const tres = (s) => s.replace(/^(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3')
  if (d.length === 9) return tres(d)
  if (d.length === 11 && d.startsWith('34')) return '+34 ' + tres(d.slice(2))
  return crudo
}
export function textoDeLaPregunta(tel, nombre) {
  const quien = String(nombre == null ? '' : nombre).replace(/\s+/g, ' ').trim()
  return quien ? t('utils.llamar.llamar_a_al', '¿Llamar a {{quien}} al {{numeroParaLeer}}?', { quien, numeroParaLeer: numeroParaLeer(tel) }) : t('utils.llamar.llamar_al', '¿Llamar al {{numeroParaLeer}}?', { numeroParaLeer: numeroParaLeer(tel) })
}

export function llamarTelefono(tel, toast, { conversacionId = null, pacienteId = null, nombre = '' } = {}) {
  let limpio = String(tel || '').replace(/\D/g, '')
  if (!limpio) { toast?.error?.(t('utils.llamar.sin_numero_de_telefono', 'Sin número de teléfono')); return Promise.resolve(false) }
  // Normalizar a internacional: los números que vienen de Gesden (citas-anuladas,
  // fichas de paciente) son NACIONALES (9 dígitos en España) y NO traen código de
  // país; los de WhatsApp sí (34XXXXXXXXX). Con el '+' de Zoiper pero sin el país,
  // se marca "+6XXXXXXXX" y la llamada no enruta. Anteponemos el país a los de 9 díg.
  const pais = leer('dial_pais', '34').replace(/\D/g, '')
  if (limpio.length === 9) limpio = pais + limpio
  const proto = leer('dial_protocol', 'tel')
  const numero = leer('dial_prefijo_mas', '') === '1' ? '+' + limpio : limpio
  const ahora = Date.now()
  if (ultima.numero === numero && ahora - ultima.at >= 0 && ahora - ultima.at < DOBLE_PULSACION_MS) return Promise.resolve(false)
  if (preguntando) return Promise.resolve(false)
  preguntando = true
  return preguntar(textoDeLaPregunta(tel, nombre))
    .then((si) => (si === true ? marcar(proto, numero, toast, conversacionId, pacienteId) !== false : false))
    .catch(() => false)
    .finally(() => { preguntando = false })
}

// LA PREGUNTA, con el diálogo de la casa. `pausaMs`: ver arriba.
function preguntar(mensaje) {
  const opciones = { titulo: t('utils.llamar.llamar_por_telefono', 'Llamar por teléfono'), mensaje, textoConfirmar: t('utils.llamar.llamar', 'Llamar'), textoCancelar: t('utils.llamar.cancelar', 'Cancelar'), tipo: 'info', pausaMs: PAUSA_PREGUNTA_MS }
  return import('../context/DialogosContext')
    .then((m) => (m && typeof m.confirmar === 'function' ? m.confirmar(opciones) : delNavegador(mensaje)), () => delNavegador(mensaje))
}
function delNavegador(mensaje) {
  try { return typeof window !== 'undefined' && typeof window.confirm === 'function' ? window.confirm(mensaje) === true : false } catch (_) { return false }
}

// MARCAR: solo después del «Llamar» de la pregunta.
function marcar(proto, numero, toast, conversacionId, pacienteId) {
  ultima = { numero, at: Date.now() }
  // Red de seguridad: si el softphone no arranca, el número queda copiado.
  try { navigator.clipboard?.writeText(numero) } catch (_) {}
  try {
    const a = document.createElement('a')
    a.href = `${proto}:${numero}`
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    a.remove()
    toast?.info?.(t('utils.llamar.llamando_a', 'Llamando a {{numero}}…', { numero }))
  } catch (e) {
    toast?.error?.(t('utils.llamar.no_se_pudo_iniciar_la', 'No se pudo iniciar la llamada. Número copiado: {{numero}}', { numero }))
    return false   // sin llamada, no hay nada que apuntar
  }
  apuntarLlamada(numero, conversacionId, pacienteId)
}

function leer(clave, porDefecto) {
  try { return localStorage.getItem(clave) || porDefecto } catch (_) { return porDefecto }
}

// Import DINÁMICO a propósito: este util no importa nada y lo cargan tal cual muchas pantallas (y sus guardias). La
// petición pasa por services/api, que pone la sesión y, en una visita del soporte, la clínica visitada (allí el
// servidor contesta que no y no se apunta nada).
function apuntarLlamada(telefono, conversacionId, pacienteId) {
  const cuerpo = { telefono, conversacionId }
  if (pacienteId != null && pacienteId !== '') cuerpo.pacienteId = pacienteId
  try {
    import('../services/api')
      .then((m) => m && m.default && m.default.post('/conversaciones/notas/llamada', cuerpo))
      .catch(() => { /* sin apuntar: la llamada ya ha salido */ })
  } catch (_) { /* sin api a mano: no se apunta */ }
}
